import { Box, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react'
import { type ReactNode, useState } from 'react'
import { FiChevronDown, FiChevronRight } from 'react-icons/fi'

import { ContextSwitchArrows } from '@/components/atoms/Icons/ContextSwitchArrows'
import type { useUserMenuContextSwitchState } from '@/components/molecules/Menu/UserMenuContent/hooks/useUserMenuContextSwitchState'
import {
  CIRCLE_LIST_MAX_HEIGHT,
  NETWORK_EXPAND_BUTTON_SIZE,
} from '@/components/molecules/Menu/UserMenuContent/hooks/useUserMenuContextSwitchState'
import { getVisibleDepth } from '@/components/molecules/Menu/UserMenuContent/userMenuContent.model'
import { CircleLogo } from '@/components/molecules/User/Circle/CircleLogo'
import { NetworkLogo } from '@/components/molecules/User/Network/NetworkLogo'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { MenuItem } from '@/components/ui/menu'
import { Tooltip } from '@/components/ui/tooltip'
import { NetworkPermissionType } from '@/graphql/api/generated'

import type { UserMenuContentProps } from '../UserMenuContent.types'

type ContextSwitchSectionProps = {
  mode: 'drawer' | 'dropdown'
  rowPx: number
  rowPy: number
  switchState: ReturnType<typeof useUserMenuContextSwitchState>
} & Pick<
  UserMenuContentProps,
  | 'user'
  | 'circles'
  | 'networks'
  | 'contextIri'
  | 'loading'
  | 'circlesLoading'
  | 'hoveredCircleId'
  | 'setHoveredCircleId'
  | 'handleSwitchContext'
  | 't'
  | 'onClose'
>

export const ContextSwitchSection = ({
  mode,
  rowPx,
  rowPy,
  switchState,
  user,
  circles,
  networks,
  contextIri,
  loading,
  circlesLoading,
  hoveredCircleId,
  setHoveredCircleId,
  handleSwitchContext,
  t,
  onClose,
}: ContextSwitchSectionProps) => {
  const registerTourNetworkSlug =
    typeof window !== 'undefined'
      ? localStorage.getItem('register_network_tour_slug')
      : null
  const [hoveredNetworkLogoId, setHoveredNetworkLogoId] = useState<
    string | null
  >(null)

  const {
    searchTerm,
    setSearchTerm,
    expandedNetworks,
    toggleNetworkExpansion,
    shouldEnableCircleFilter,
    filteredCircles,
    groupedNetworkCircles,
    hasNetworkGroups,
    standaloneContextCircles,
    visibleStandaloneCircleIds,
    visibleFilteredCircleIds,
    circleParentById,
  } = switchState

  const onSwitchUser = async () => {
    await handleSwitchContext(null)
    onClose?.()
  }

  const onSwitchCircle =
    (circle: NonNullable<(typeof circles)[number]>) => async () => {
      await handleSwitchContext(circle.id, circle.name)
      onClose?.()
    }

  const canSwitchNetworkContext = (network: (typeof networks)[number]) => {
    const membership = network.currentUserMemberships?.edges
      ?.map((edge) => edge?.node)
      .find((node) => node?.user?.id === user.id)
    return (
      membership?.permission === NetworkPermissionType.Owner ||
      membership?.permission === NetworkPermissionType.Administrator
    )
  }

  const getExpandButtonLabel = (isExpanded: boolean) =>
    isExpanded ? 'Collapse network contexts' : 'Expand network contexts'

  if (circlesLoading || (circles.length === 0 && networks.length === 0)) {
    return null
  }

  const listShell = (children: ReactNode) => (
    <>
      <Box px={4} py={2} data-tour="user-menu-context-switch-section">
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          {t('context.switchTo')}
        </Text>
      </Box>
      <VStack
        align="stretch"
        gap={0}
        maxH={shouldEnableCircleFilter ? CIRCLE_LIST_MAX_HEIGHT : undefined}
        overflowY={shouldEnableCircleFilter ? 'auto' : undefined}
      >
        {shouldEnableCircleFilter && (
          <Box px={4} py={2}>
            <Input
              size="sm"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('context.communitySearchPlaceholder')}
            />
          </Box>
        )}
        {children}
      </VStack>
    </>
  )

  if (mode === 'drawer') {
    const ButtonRow = ({
      onClick,
      children,
      disabled,
    }: {
      onClick: () => void
      children: React.ReactNode
      disabled?: boolean
    }) => (
      <Button
        onClick={onClick}
        disabled={disabled}
        px={rowPx}
        py={rowPy}
        w="full"
        textAlign="left"
        cursor={disabled ? 'wait' : 'pointer'}
        opacity={disabled ? 0.6 : 1}
        _hover={disabled ? undefined : { bg: 'gray.100' }}
        _dark={{
          _hover: disabled ? undefined : { bg: 'gray.800' },
        }}
      >
        {children}
      </Button>
    )

    return listShell(
      <>
        <ButtonRow onClick={onSwitchUser} disabled={loading || !contextIri}>
          <HStack
            gap={3}
            w="full"
            onMouseEnter={() => setHoveredCircleId('user')}
            onMouseLeave={() => setHoveredCircleId(null)}
          >
            <Box position="relative" display="inline-block">
              <Avatar
                size="sm"
                src={user?.avatar?.contentUrl || undefined}
                aria-label={`avatar ${user?.firstname}`}
              />
              {hoveredCircleId === 'user' && (
                <>
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="blackAlpha.500"
                    borderRadius="full"
                    zIndex={1}
                  />
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    pointerEvents="none"
                    zIndex={2}
                    style={{
                      animation: 'rotate-arrows 1s linear infinite',
                    }}
                  >
                    <ContextSwitchArrows size={10} color="white" />
                  </Box>
                </>
              )}
            </Box>
            <VStack alignItems="flex-start" gap={0} flex={1}>
              <Text fontSize="sm" fontWeight="medium">
                {t('context.user', { name: user?.firstname || '' })}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {t('context.personal')}
              </Text>
            </VStack>
            {!contextIri && (
              <Box color="brand.fg">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <circle cx="8" cy="8" r="6" fill="currentColor" />
                </svg>
              </Box>
            )}
          </HStack>
        </ButtonRow>
        {hasNetworkGroups ? (
          <>
            {groupedNetworkCircles.map(
              ({ network, circles: networkCircles }) => {
                const hasChildCircles = networkCircles.length > 0
                const isExpanded =
                  expandedNetworks[network.id] ?? contextIri === network.id
                const isNetworkActive = contextIri === network.id
                const isNetworkSwitchable = canSwitchNetworkContext(network)
                return (
                  <VStack key={network.id} align="stretch" gap={0}>
                    <ButtonRow
                      onClick={() => {
                        toggleNetworkExpansion(network.id)
                      }}
                      disabled={loading}
                    >
                      <HStack gap={3} w="full">
                        {hasChildCircles ? (
                          <IconButton
                            variant="ghost"
                            size="sm"
                            aria-label={getExpandButtonLabel(isExpanded)}
                            minW={NETWORK_EXPAND_BUTTON_SIZE}
                            w={NETWORK_EXPAND_BUTTON_SIZE}
                            h={NETWORK_EXPAND_BUTTON_SIZE}
                            color="fg.muted"
                            onClick={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                              toggleNetworkExpansion(network.id)
                            }}
                          >
                            {isExpanded ? (
                              <FiChevronDown size={16} />
                            ) : (
                              <FiChevronRight size={16} />
                            )}
                          </IconButton>
                        ) : (
                          <Box
                            w={NETWORK_EXPAND_BUTTON_SIZE}
                            h={NETWORK_EXPAND_BUTTON_SIZE}
                            minW={NETWORK_EXPAND_BUTTON_SIZE}
                            flexShrink={0}
                            aria-hidden
                          />
                        )}
                        <Tooltip
                          disabled={!isNetworkSwitchable}
                          content={t('context.switchByClickingNetworkLogo')}
                          showArrow
                        >
                          <HStack
                            gap={3}
                            flex={1}
                            onMouseEnter={() =>
                              setHoveredCircleId(`network:${network.id}`)
                            }
                            onMouseLeave={() => setHoveredCircleId(null)}
                          >
                            <Box
                              position="relative"
                              display="inline-block"
                              onMouseEnter={() =>
                                setHoveredNetworkLogoId(network.id)
                              }
                              onMouseLeave={() => setHoveredNetworkLogoId(null)}
                              onClick={(event) => {
                                if (!isNetworkSwitchable) {
                                  return
                                }
                                event.preventDefault()
                                event.stopPropagation()
                                void handleSwitchContext(
                                  network.id,
                                  network.name
                                )
                              }}
                              cursor={
                                isNetworkSwitchable ? 'pointer' : 'default'
                              }
                            >
                              <NetworkLogo network={network} size="sm" />
                              {hoveredCircleId === `network:${network.id}` && (
                                <>
                                  <Box
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="blackAlpha.500"
                                    borderRadius="full"
                                    zIndex={1}
                                  />
                                  <Box
                                    position="absolute"
                                    top="50%"
                                    left="50%"
                                    transform="translate(-50%, -50%)"
                                    pointerEvents="none"
                                    zIndex={2}
                                    style={
                                      hoveredNetworkLogoId === network.id
                                        ? {
                                            animation:
                                              'rotate-arrows 1s linear infinite',
                                          }
                                        : undefined
                                    }
                                  >
                                    <ContextSwitchArrows
                                      size={10}
                                      color="white"
                                    />
                                  </Box>
                                </>
                              )}
                            </Box>
                            <VStack alignItems="flex-start" gap={0} flex={1}>
                              <Text fontSize="sm" fontWeight="medium">
                                {network.name}
                              </Text>
                              <Text fontSize="xs" color="fg.muted">
                                {t('context.network')}
                              </Text>
                            </VStack>
                          </HStack>
                        </Tooltip>
                        {isNetworkActive && (
                          <Box color="brand.fg">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <circle cx="8" cy="8" r="6" fill="currentColor" />
                            </svg>
                          </Box>
                        )}
                      </HStack>
                    </ButtonRow>
                    {hasChildCircles &&
                      isExpanded &&
                      networkCircles.map((circle) => {
                        const isActive = contextIri === circle.id
                        const isHovered = hoveredCircleId === circle.id
                        return (
                          <ButtonRow
                            key={circle.id}
                            onClick={onSwitchCircle(circle)}
                            disabled={loading}
                          >
                            <HStack
                              gap={3}
                              w="full"
                              pl={8}
                              onMouseEnter={() => setHoveredCircleId(circle.id)}
                              onMouseLeave={() => setHoveredCircleId(null)}
                            >
                              <Box position="relative" display="inline-block">
                                <CircleLogo circle={circle} size="sm" />
                                {isHovered && (
                                  <>
                                    <Box
                                      position="absolute"
                                      top="0"
                                      left="0"
                                      right="0"
                                      bottom="0"
                                      bg="blackAlpha.500"
                                      borderRadius="full"
                                      zIndex={1}
                                    />
                                    <Box
                                      position="absolute"
                                      top="50%"
                                      left="50%"
                                      transform="translate(-50%, -50%)"
                                      pointerEvents="none"
                                      zIndex={2}
                                      style={{
                                        animation:
                                          'rotate-arrows 1s linear infinite',
                                      }}
                                    >
                                      <ContextSwitchArrows
                                        size={10}
                                        color="white"
                                      />
                                    </Box>
                                  </>
                                )}
                              </Box>
                              <VStack alignItems="flex-start" gap={0} flex={1}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {circle.name}
                                </Text>
                                <Text fontSize="xs" color="fg.muted">
                                  {t('context.circle')}
                                </Text>
                              </VStack>
                              {isActive && (
                                <Box color="brand.fg">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                  >
                                    <circle
                                      cx="8"
                                      cy="8"
                                      r="6"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </Box>
                              )}
                            </HStack>
                          </ButtonRow>
                        )
                      })}
                  </VStack>
                )
              }
            )}
            {standaloneContextCircles.map((circle) => {
              if (!circle) return null
              const isActive = contextIri === circle.id
              return (
                <ButtonRow
                  key={circle.id}
                  onClick={onSwitchCircle(circle)}
                  disabled={loading}
                >
                  <HStack gap={3} w="full">
                    <CircleLogo circle={circle} size="sm" />
                    <VStack alignItems="flex-start" gap={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        {circle.name}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {t('context.circle')}
                      </Text>
                    </VStack>
                    {isActive && (
                      <Box color="brand.fg">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <circle cx="8" cy="8" r="6" fill="currentColor" />
                        </svg>
                      </Box>
                    )}
                  </HStack>
                </ButtonRow>
              )
            })}
          </>
        ) : filteredCircles.length === 0 && shouldEnableCircleFilter ? (
          <Box px={4} py={2}>
            <Text fontSize="sm" color="fg.muted">
              {t('global:noResult')}
            </Text>
          </Box>
        ) : (
          filteredCircles.map((circle) => {
            if (!circle) return null
            const isActive = contextIri === circle.id
            return (
              <ButtonRow
                key={circle.id}
                onClick={onSwitchCircle(circle)}
                disabled={loading}
              >
                <HStack gap={3} w="full">
                  <CircleLogo circle={circle} size="sm" />
                  <VStack alignItems="flex-start" gap={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {circle.name}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {t('context.circle')}
                    </Text>
                  </VStack>
                  {isActive && (
                    <Box color="brand.fg">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <circle cx="8" cy="8" r="6" fill="currentColor" />
                      </svg>
                    </Box>
                  )}
                </HStack>
              </ButtonRow>
            )
          })
        )}
      </>
    )
  }

  return listShell(
    <>
      <MenuItem
        px={rowPx}
        py={rowPy}
        cursor={loading ? 'wait' : 'pointer'}
        value="switch-context-user"
        onClick={onSwitchUser}
        disabled={loading || !contextIri}
        bg={!contextIri ? 'brand.50' : undefined}
        _dark={{
          bg: !contextIri ? 'brand.900' : undefined,
        }}
        _hover={{
          bg: 'brand.100',
          _dark: { bg: 'brand.800' },
        }}
        onMouseEnter={() => setHoveredCircleId('user')}
        onMouseLeave={() => setHoveredCircleId(null)}
      >
        <HStack gap={3} w="full">
          <Box position="relative" display="inline-block">
            <Avatar
              size="sm"
              src={user?.avatar?.contentUrl || undefined}
              aria-label={`avatar ${user?.firstname}`}
            />
            {hoveredCircleId === 'user' && (
              <>
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="blackAlpha.500"
                  borderRadius="full"
                  zIndex={1}
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  pointerEvents="none"
                  zIndex={2}
                  style={{
                    animation: 'rotate-arrows 1s linear infinite',
                  }}
                >
                  <ContextSwitchArrows size={10} color="white" />
                </Box>
              </>
            )}
          </Box>
          <VStack alignItems="flex-start" gap={0} flex={1}>
            <Text fontSize="sm" fontWeight="medium">
              {t('context.user', { name: user?.firstname || '' })}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {t('context.personal')}
            </Text>
          </VStack>
          {!contextIri && (
            <Box color="brand.fg">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <circle cx="8" cy="8" r="6" fill="currentColor" />
              </svg>
            </Box>
          )}
        </HStack>
      </MenuItem>
      {hasNetworkGroups ? (
        <>
          {groupedNetworkCircles.map(({ network, circles: networkCircles }) => {
            const hasChildCircles = networkCircles.length > 0
            const isExpanded =
              expandedNetworks[network.id] ?? contextIri === network.id
            const isNetworkActive = contextIri === network.id
            const isNetworkSwitchable = canSwitchNetworkContext(network)
            const visibleNetworkCircleIds = new Set(
              networkCircles.map((networkCircle) => networkCircle.id)
            )
            return (
              <VStack key={network.id} align="stretch" gap={0}>
                <MenuItem
                  px={rowPx}
                  py={rowPy}
                  cursor={loading ? 'wait' : 'pointer'}
                  value={`switch-network-context-${network.id}`}
                  closeOnSelect={false}
                  data-tour={
                    network.slug === registerTourNetworkSlug
                      ? 'user-menu-context-switch-network-item'
                      : undefined
                  }
                  onClick={() => {
                    toggleNetworkExpansion(network.id)
                  }}
                  disabled={loading}
                  bg={isNetworkActive ? 'brand.50' : undefined}
                  _dark={{
                    bg: isNetworkActive ? 'brand.900' : undefined,
                  }}
                  _hover={{
                    bg: 'brand.100',
                    _dark: { bg: 'brand.800' },
                  }}
                >
                  <HStack gap={3} w="full">
                    {hasChildCircles ? (
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={getExpandButtonLabel(isExpanded)}
                        minW={NETWORK_EXPAND_BUTTON_SIZE}
                        w={NETWORK_EXPAND_BUTTON_SIZE}
                        h={NETWORK_EXPAND_BUTTON_SIZE}
                        color="fg.muted"
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          toggleNetworkExpansion(network.id)
                        }}
                      >
                        {isExpanded ? (
                          <FiChevronDown size={16} />
                        ) : (
                          <FiChevronRight size={16} />
                        )}
                      </IconButton>
                    ) : (
                      <Box
                        w={NETWORK_EXPAND_BUTTON_SIZE}
                        h={NETWORK_EXPAND_BUTTON_SIZE}
                        minW={NETWORK_EXPAND_BUTTON_SIZE}
                        flexShrink={0}
                        aria-hidden
                      />
                    )}
                    <Tooltip
                      disabled={!isNetworkSwitchable}
                      content={t('context.switchByClickingNetworkLogo')}
                      showArrow
                    >
                      <HStack
                        gap={3}
                        flex={1}
                        onMouseEnter={() =>
                          setHoveredCircleId(`network:${network.id}`)
                        }
                        onMouseLeave={() => setHoveredCircleId(null)}
                      >
                        <Box
                          position="relative"
                          display="inline-block"
                          onMouseEnter={() =>
                            setHoveredNetworkLogoId(network.id)
                          }
                          onMouseLeave={() => setHoveredNetworkLogoId(null)}
                          onClick={(event) => {
                            if (!isNetworkSwitchable) {
                              return
                            }
                            event.preventDefault()
                            event.stopPropagation()
                            void handleSwitchContext(network.id, network.name)
                          }}
                          cursor={isNetworkSwitchable ? 'pointer' : 'default'}
                        >
                          <NetworkLogo network={network} size="sm" />
                          {hoveredCircleId === `network:${network.id}` && (
                            <>
                              <Box
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                bg="blackAlpha.500"
                                borderRadius="full"
                                zIndex={1}
                              />
                              <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                pointerEvents="none"
                                zIndex={2}
                                style={
                                  hoveredNetworkLogoId === network.id
                                    ? {
                                        animation:
                                          'rotate-arrows 1s linear infinite',
                                      }
                                    : undefined
                                }
                              >
                                <ContextSwitchArrows size={10} color="white" />
                              </Box>
                            </>
                          )}
                        </Box>
                        <VStack alignItems="flex-start" gap={0} flex={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            {network.name}
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            {t('context.network')}
                          </Text>
                        </VStack>
                      </HStack>
                    </Tooltip>
                    {isNetworkActive && (
                      <Box color="brand.fg">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <circle cx="8" cy="8" r="6" fill="currentColor" />
                        </svg>
                      </Box>
                    )}
                  </HStack>
                </MenuItem>
                {hasChildCircles &&
                  isExpanded &&
                  networkCircles.map((circle) => {
                    const isActive = contextIri === circle.id
                    const isHovered = hoveredCircleId === circle.id
                    const depth = getVisibleDepth(
                      circle.id,
                      visibleNetworkCircleIds,
                      circleParentById
                    )
                    return (
                      <MenuItem
                        key={circle.id}
                        px={rowPx}
                        py={rowPy}
                        cursor={loading ? 'wait' : 'pointer'}
                        value={`switch-context-${circle.id}`}
                        onClick={() =>
                          handleSwitchContext(circle.id, circle.name)
                        }
                        disabled={loading}
                        bg={isActive ? 'brand.50' : undefined}
                        _dark={{
                          bg: isActive ? 'brand.900' : undefined,
                        }}
                        _hover={{
                          bg: 'brand.100',
                          _dark: { bg: 'brand.800' },
                        }}
                        onMouseEnter={() => setHoveredCircleId(circle.id)}
                        onMouseLeave={() => setHoveredCircleId(null)}
                      >
                        <HStack gap={3} w="full" pl={8 + depth * 4}>
                          {depth > 0 ? (
                            <Text fontSize="xs" color="fg.muted" minW="8px">
                              └
                            </Text>
                          ) : null}
                          <Box position="relative" display="inline-block">
                            <CircleLogo circle={circle} size="sm" />
                            {isHovered && (
                              <>
                                <Box
                                  position="absolute"
                                  top="0"
                                  left="0"
                                  right="0"
                                  bottom="0"
                                  bg="blackAlpha.500"
                                  borderRadius="full"
                                  zIndex={1}
                                />
                                <Box
                                  position="absolute"
                                  top="50%"
                                  left="50%"
                                  transform="translate(-50%, -50%)"
                                  pointerEvents="none"
                                  zIndex={2}
                                  style={{
                                    animation:
                                      'rotate-arrows 1s linear infinite',
                                  }}
                                >
                                  <ContextSwitchArrows
                                    size={10}
                                    color="white"
                                  />
                                </Box>
                              </>
                            )}
                          </Box>
                          <VStack alignItems="flex-start" gap={0} flex={1}>
                            <Text fontSize="sm" fontWeight="medium">
                              {circle.name}
                            </Text>
                          </VStack>
                          {isActive && (
                            <Box color="brand.fg">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                              >
                                <circle
                                  cx="8"
                                  cy="8"
                                  r="6"
                                  fill="currentColor"
                                />
                              </svg>
                            </Box>
                          )}
                        </HStack>
                      </MenuItem>
                    )
                  })}
              </VStack>
            )
          })}
          {standaloneContextCircles.map((circle) => {
            if (!circle) return null
            const isActive = contextIri === circle.id
            const isHovered = hoveredCircleId === circle.id
            const depth = getVisibleDepth(
              circle.id,
              visibleStandaloneCircleIds,
              circleParentById
            )
            return (
              <MenuItem
                key={circle.id}
                px={rowPx}
                py={rowPy}
                cursor={loading ? 'wait' : 'pointer'}
                value={`switch-context-${circle.id}`}
                onClick={() => handleSwitchContext(circle.id, circle.name)}
                disabled={loading}
                bg={isActive ? 'brand.50' : undefined}
                _dark={{ bg: isActive ? 'brand.900' : undefined }}
                _hover={{
                  bg: 'brand.100',
                  _dark: { bg: 'brand.800' },
                }}
                onMouseEnter={() => setHoveredCircleId(circle.id)}
                onMouseLeave={() => setHoveredCircleId(null)}
              >
                <HStack gap={3} w="full" pl={depth * 4}>
                  {depth > 0 ? (
                    <Text fontSize="xs" color="fg.muted" minW="8px">
                      └
                    </Text>
                  ) : null}
                  <Box position="relative" display="inline-block">
                    <CircleLogo circle={circle} size="sm" />
                    {isHovered && (
                      <>
                        <Box
                          position="absolute"
                          top="0"
                          left="0"
                          right="0"
                          bottom="0"
                          bg="blackAlpha.500"
                          borderRadius="full"
                          zIndex={1}
                        />
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          pointerEvents="none"
                          zIndex={2}
                          style={{
                            animation: 'rotate-arrows 1s linear infinite',
                          }}
                        >
                          <ContextSwitchArrows size={10} color="white" />
                        </Box>
                      </>
                    )}
                  </Box>
                  <VStack alignItems="flex-start" gap={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {circle.name}
                    </Text>
                  </VStack>
                  {isActive && (
                    <Box color="brand.fg">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <circle cx="8" cy="8" r="6" fill="currentColor" />
                      </svg>
                    </Box>
                  )}
                </HStack>
              </MenuItem>
            )
          })}
        </>
      ) : filteredCircles.length === 0 && shouldEnableCircleFilter ? (
        <Box px={4} py={2}>
          <Text fontSize="sm" color="fg.muted">
            {t('global:noResult')}
          </Text>
        </Box>
      ) : (
        filteredCircles.map((circle) => {
          if (!circle) return null
          const isActive = contextIri === circle.id
          const isHovered = hoveredCircleId === circle.id
          const depth = getVisibleDepth(
            circle.id,
            visibleFilteredCircleIds,
            circleParentById
          )
          return (
            <MenuItem
              key={circle.id}
              px={rowPx}
              py={rowPy}
              cursor={loading ? 'wait' : 'pointer'}
              value={`switch-context-${circle.id}`}
              onClick={() => handleSwitchContext(circle.id, circle.name)}
              disabled={loading}
              bg={isActive ? 'brand.50' : undefined}
              _dark={{ bg: isActive ? 'brand.900' : undefined }}
              _hover={{
                bg: 'brand.100',
                _dark: { bg: 'brand.800' },
              }}
              onMouseEnter={() => setHoveredCircleId(circle.id)}
              onMouseLeave={() => setHoveredCircleId(null)}
            >
              <HStack gap={3} w="full" pl={depth * 4}>
                {depth > 0 ? (
                  <Text fontSize="xs" color="fg.muted" minW="8px">
                    └
                  </Text>
                ) : null}
                <Box position="relative" display="inline-block">
                  <CircleLogo circle={circle} size="sm" />
                  {isHovered && (
                    <>
                      <Box
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="blackAlpha.500"
                        borderRadius="full"
                        zIndex={1}
                      />
                      <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        pointerEvents="none"
                        zIndex={2}
                        style={{
                          animation: 'rotate-arrows 1s linear infinite',
                        }}
                      >
                        <ContextSwitchArrows size={10} color="white" />
                      </Box>
                    </>
                  )}
                </Box>
                <VStack alignItems="flex-start" gap={0} flex={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    {circle.name}
                  </Text>
                </VStack>
                {isActive && (
                  <Box color="brand.fg">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <circle cx="8" cy="8" r="6" fill="currentColor" />
                    </svg>
                  </Box>
                )}
              </HStack>
            </MenuItem>
          )
        })
      )}
    </>
  )
}
